<script lang="ts">
  /**
   * Compose formatting toolbar (spec §11): bold / italic / underline | bullets, link, image |
   * clear format | insert template, save as template. `mousedown` is prevented so the editor keeps
   * its selection; the owner restores the remembered range before running each command.
   */
  import { t } from '$lib/i18n';
  import { tooltip } from '$lib/util/dom';
  import Icon from '../ui/Icon.svelte';
  import { clearFileInput, fileInputFiles, type EditorCommand } from './editor';

  let {
    el = $bindable(),
    tplOpen = false,
    tplMenuId,
    onCommand,
    onLink,
    onImage,
    onTemplate,
    onSaveTemplate,
  }: {
    /** The toolbar element (the owner lifts it above the mobile keyboard). */
    el?: HTMLElement;
    tplOpen?: boolean;
    tplMenuId: string;
    onCommand: (command: EditorCommand) => void;
    onLink: () => void;
    onImage: (file: File) => void;
    onTemplate: () => void;
    onSaveTemplate: () => void;
  } = $props();

  function onImageChange(ev: Event): void {
    const file = fileInputFiles(ev)[0];
    clearFileInput(ev);
    if (file) onImage(file);
  }
</script>

<div class="toolbar" role="toolbar" tabindex="-1" bind:this={el} onmousedown={(ev) => ev.preventDefault()}>
  <button type="button" aria-label={t('bold')} use:tooltip onclick={() => onCommand('bold')}><b>B</b></button>
  <button type="button" aria-label={t('italic')} use:tooltip onclick={() => onCommand('italic')}><i>I</i></button>
  <button type="button" aria-label={t('underline')} use:tooltip onclick={() => onCommand('underline')}><u>U</u></button>
  <span class="sep"></span>
  <button type="button" aria-label={t('bullets')} use:tooltip onclick={() => onCommand('insertUnorderedList')}><Icon name="list" size={15} /></button>
  <button type="button" aria-label={t('addLink')} use:tooltip onclick={onLink}><Icon name="link" size={15} /></button>
  <label class="imgbtn" aria-label={t('addImage')} use:tooltip>
    <Icon name="image" size={15} />
    <input class="filein" type="file" accept="image/*" onchange={onImageChange} />
  </label>
  <span class="sep"></span>
  <button type="button" aria-label={t('clearFmt')} use:tooltip onclick={() => onCommand('removeFormat')}><Icon name="eraser" size={15} /></button>
  <span class="sep"></span>
  <button
    type="button"
    class="tpl-btn"
    aria-haspopup="menu"
    aria-label={t('insertTemplate')}
    aria-controls={tplOpen ? tplMenuId : undefined}
    aria-expanded={tplOpen}
    use:tooltip
    onclick={onTemplate}><Icon name="draft" size={15} /></button>
  <button type="button" class="tpl-save" aria-label={t('saveAsTemplate')} use:tooltip onclick={onSaveTemplate}><Icon name="plus" size={15} /></button>
</div>

<style>
  /* ===== component: svelte-lveo7m (Compose) — toolbar rules ===== */
  .toolbar {
    flex: none;
  }

  .toolbar {
    display: flex;
    align-items: center;
    gap: 2px;
    border-bottom: 1px solid var(--border-subtle);
    padding: var(--space-1) var(--space-3);
    background: var(--surface-subtle);
    position: sticky;
    top: 0;
    z-index: var(--z-sticky);
    min-height: var(--control-md);
  }

  .toolbar button,
  .toolbar .imgbtn {
    min-width: var(--control-sm);
    height: var(--control-sm);
    display: inline-grid;
    place-items: center;
    border-radius: var(--radius-xs);
    color: var(--text-body);
    font-size: var(--text-sm);
    cursor: pointer;
    padding: 0 var(--space-1);
    position: relative;
  }

  @media (max-width: 900px) and (pointer: coarse) {
    .toolbar button,
    .toolbar .imgbtn,
    .tpl-btn,
    .tpl-save {
      min-width: 44px;
      min-height: 44px;
      height: auto;
    }
  }

  .toolbar button:hover,
  .toolbar .imgbtn:hover {
    background: var(--surface-sunken);
  }

  .toolbar button:active,
  .toolbar .imgbtn:active {
    background: var(--surface-sunken);
    transform: scale(0.94);
  }

  .toolbar .imgbtn:focus-within {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }

  .filein {
    position: absolute;
    inline-size: 1px;
    block-size: 1px;
    opacity: 0;
  }

  .sep {
    width: 1px;
    height: 20px;
    background: var(--border-default);
    margin-inline: var(--space-1);
  }

  @media (max-width: 900px) {
    .toolbar {
      position: fixed;
      inset-inline: 0;
      top: auto;
      bottom: 0;
      border-top: 1px solid var(--border-subtle);
      border-bottom: none;
      padding-bottom: calc(var(--space-1) + env(safe-area-inset-bottom));
      z-index: var(--z-nav);
      will-change: transform;
      background: var(--surface-card);
    }
  }

  /* compose toolbar (fixed at the bottom on phones): two even rows, every control ≥44px, nothing hidden */
  @media (max-width: 480px) {
    .toolbar {
      flex-wrap: wrap;
      gap: 0;
      padding-inline: var(--space-2);
    }

    .toolbar .sep {
      display: none;
    }

    .toolbar > button,
    .toolbar > .imgbtn {
      flex: 1 1 20%;
      min-height: var(--control-lg);
      height: auto;
      padding: 0;
    }
  }
</style>
